import { useState, useCallback, useRef } from 'react'
import type { ChatMessage } from '../../shared/types/chat'
import { mapApiMessagesToChat } from './utils'
import * as api from './api'

const SIDEBAR_COLLAPSED_KEY = 'tendbot-sidebar-collapsed'

interface UseConversationManagerParams {
  conversationId: string | null
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  refetchConversations: () => Promise<void>
  streamCompletedIds: React.MutableRefObject<Set<string>>
}

export interface UseConversationManagerResult {
  drawerOpen: boolean
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  sidebarCollapsed: boolean
  toggleSidebarCollapsed: () => void
  handleNewChat: () => Promise<void>
  switchToConversation: (targetId: string) => Promise<void>
}

export function useConversationManager({
  conversationId,
  setConversationId,
  setMessages,
  refetchConversations,
  streamCompletedIds,
}: UseConversationManagerParams): UseConversationManagerResult {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true' } catch { return false }
  })
  const switchRef = useRef<string | null>(null)

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)) } catch { /* localStorage unavailable */ }
      return next
    })
  }, [])

  const handleNewChat = useCallback(async () => {
    try {
      const { conversationId: newId } = await api.createNewConversation()
      setConversationId(newId)
      setMessages([])
      streamCompletedIds.current.clear()
      await refetchConversations()
    } catch (err) {
      console.error('Failed to create new conversation:', err)
    }
  }, [setConversationId, setMessages, refetchConversations, streamCompletedIds])

  const switchToConversation = useCallback(async (targetId: string) => {
    if (targetId === conversationId) return
    switchRef.current = targetId
    setConversationId(targetId)
    setMessages([])
    streamCompletedIds.current.clear()
    try {
      const { messages: apiMessages } = await api.getMessages(targetId)
      // Guard against stale responses from rapid switching
      if (switchRef.current !== targetId) return
      const mapped = mapApiMessagesToChat(apiMessages)
      for (const msg of mapped) streamCompletedIds.current.add(msg.id)
      setMessages(mapped)
    } catch (err) {
      console.error('Failed to load conversation:', err)
    }
  }, [conversationId, setConversationId, setMessages, streamCompletedIds])

  return {
    drawerOpen,
    setDrawerOpen,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    handleNewChat,
    switchToConversation,
  }
}
